import React, { useState } from 'react';
import { Card, Table, Modal, Button, Form, Spinner, Alert } from 'react-bootstrap';
import { formatDate } from '../../utils/dateFormatter';
import { useMedia } from '../../services/api/hooks/useMedia';
import { useConfigBasedTimeFilter, getStatusText, getToggleButtonText } from '../../utils/timeFilter';

interface MediaItem {
  id: number;
  date_added: string;
  name: string;
  r2_filename: string;
  public_url: string;
}

interface ImageStats {
  fileSize: number;
  width: number;
  height: number;
  aspectRatio: string;
  fileType: string;
}

const Media = () => {
  const { showAll, setShowAll, timeFilter, isLoading: timeFilterLoading } = useConfigBasedTimeFilter();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const { data: mediaList, totalMedia, loading, error: apiError, uploadMedia, deleteMedia } = useMedia(showAll, currentPage, itemsPerPage);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [mediaToDelete, setMediaToDelete] = useState<MediaItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Upload form state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [mediaName, setMediaName] = useState('');
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [imageStats, setImageStats] = useState<ImageStats | null>(null);
  const [error, setError] = useState<string | null>(null);





  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Invalid file type. Please select a JPEG, PNG, GIF, or WebP image.');
      return;
    }

    // Validate file size (10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('File size exceeds 10MB limit.');
      return;
    }

    setSelectedFile(file);
    setError(null);

    // Set default name from filename
    const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
    setMediaName(fileNameWithoutExt);

    // Read file for preview and get dimensions
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const aspectRatio = (img.width / img.height).toFixed(2);
        setImageStats({
          fileSize: file.size,
          width: img.width,
          height: img.height,
          aspectRatio: `${aspectRatio}:1`,
          fileType: file.type,
        });
        setImageSrc(reader.result as string);
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  };



  // Handle upload
  const handleUpload = async () => {
    if (!selectedFile || !mediaName.trim()) {
      setError('Please complete all fields');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      // Create FormData
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('name', mediaName);

      // Upload to backend
      await uploadMedia(formData);

      // Reset form and close modal
      setShowUploadModal(false);
      resetUploadForm();
      setCurrentPage(1); // Reset to first page after upload
    } catch (error: any) {
      console.error('Error uploading media:', error);
      setError(error.message || 'Failed to upload media');
    } finally {
      setIsUploading(false);
    }
  };

  const resetUploadForm = () => {
    setSelectedFile(null);
    setMediaName('');
    setImageSrc(null);
    setImageStats(null);
    setError(null);
  };

  // Handle delete
  const confirmDelete = (media: MediaItem) => {
    setMediaToDelete(media);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!mediaToDelete) return;

    setIsDeleting(true);
    try {
      await deleteMedia(mediaToDelete.id);
      setShowDeleteModal(false);
      setMediaToDelete(null);
      // Stay on current page unless it becomes empty
      if (mediaList.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    } catch (error) {
      console.error('Error deleting media:', error);
      setError('Failed to delete media');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <Card className="m-3">
      <Card.Header as="h5" className="d-flex justify-content-between align-items-center" style={{ backgroundColor: '#e1bee7' }}>
        <div className="d-flex align-items-center gap-3">
          <span>Media Management</span>
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted small">
              {getStatusText(showAll, timeFilter)}
            </span>
            <Button 
              variant="outline-secondary" 
              size="sm"
              onClick={() => setShowAll(!showAll)}
              disabled={timeFilterLoading}
            >
              {getToggleButtonText(showAll, timeFilter)}
            </Button>
          </div>
        </div>
        <Button variant="primary" onClick={() => setShowUploadModal(true)}>
          Add Media
        </Button>
      </Card.Header>
      <Card.Body>

        {(error || apiError) && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error || apiError}
          </Alert>
        )}

        {loading ? (
          <div className="text-center p-5">
            <Spinner animation="border" />
            <p className="mt-2">Loading media...</p>
          </div>
        ) : mediaList.length === 0 ? (
          <p>No media found. Create one above!</p>
        ) : (
          <>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Public URL</th>
                  <th>Date Added</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {mediaList.map((media) => (
                  <tr key={media.id}>
                    <td>{media.id}</td>
                    <td>{media.name}</td>
                    <td>
                      <a
                        href={media.public_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {media.public_url}
                      </a>
                    </td>
                    <td>{formatDate(media.date_added)}</td>
                    <td>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => confirmDelete(media)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            
            {Math.ceil(totalMedia / itemsPerPage) > 1 && (
              <div className="d-flex justify-content-center mt-3">
                <Button
                  variant="outline-primary"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  className="me-2"
                >
                  Previous
                </Button>
                <span className="align-self-center mx-3">
                  Page {currentPage} of {Math.ceil(totalMedia / itemsPerPage)}
                </span>
                <Button
                  variant="outline-primary"
                  size="sm"
                  disabled={currentPage === Math.ceil(totalMedia / itemsPerPage)}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  className="ms-2"
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </Card.Body>

      {/* Upload Modal */}
      <Modal
        show={showUploadModal}
        onHide={() => {
          setShowUploadModal(false);
          resetUploadForm();
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Upload New Image</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Select Image</Form.Label>
            <Form.Control
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleFileChange}
              disabled={isUploading}
            />
            <Form.Text className="text-muted">
              Accepted formats: JPEG, PNG, GIF, WebP. Max size: 10MB
            </Form.Text>
          </Form.Group>

          {imageStats && (
            <Alert variant="info" className="mb-3">
              <strong>Image Statistics:</strong>
              <ul className="mb-0 mt-2">
                <li>File Size: {formatFileSize(imageStats.fileSize)}</li>
                <li>Dimensions: {imageStats.width} × {imageStats.height} px</li>
                <li>Aspect Ratio: {imageStats.aspectRatio}</li>
                <li>File Type: {imageStats.fileType}</li>
              </ul>
            </Alert>
          )}

          {imageSrc && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Image Name</Form.Label>
                <Form.Control
                  type="text"
                  value={mediaName}
                  onChange={(e) => setMediaName(e.target.value)}
                  disabled={isUploading}
                  placeholder="Enter a name for this image"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Preview</Form.Label>
                <div className="text-center">
                  <img
                    src={imageSrc}
                    alt="Preview"
                    style={{
                      maxWidth: '100%',
                      /* maxHeight: '300px', */
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                  />
                </div>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowUploadModal(false);
              resetUploadForm();
            }}
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpload}
            disabled={!selectedFile || !mediaName.trim() || isUploading}
          >
            {isUploading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Uploading...
              </>
            ) : (
              'Upload'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete "{mediaToDelete?.name}"? This action cannot be
          undone.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default Media;
